import { Info } from 'lucide-react';
import type { ReactNode } from 'react';

interface InfoHintProps {
  /** Tooltip content shown on hover. */
  children: ReactNode;
  /** aria-label for the icon trigger. */
  label?: string;
  /** Horizontal alignment of the tooltip relative to the icon. */
  align?: 'left' | 'right';
  className?: string;
}

/**
 * Small info (i) icon that reveals help on hover via a pure-CSS tooltip —
 * instant (no native-title delay) and no help cursor.
 *
 * Caveat: the tooltip is absolutely positioned, so the icon must live in a
 * container that is NOT `overflow-hidden` (e.g. sidebar header, panel section
 * title) or the tooltip will be clipped. For clipped contexts (virtualized
 * list rows) fall back to a native `title`.
 */
export function InfoHint({ children, label = 'More information', align = 'right', className = '' }: InfoHintProps) {
  return (
    <span className={`group relative inline-flex flex-none items-center ${className}`}>
      <Info aria-label={label} className="w-3.5 h-3.5 text-icon-subtle transition-colors group-hover:text-content-tertiary" />
      <span
        role="tooltip"
        className={`pointer-events-none absolute top-full z-30 mt-1 hidden w-60 rounded-md border border-border-secondary bg-bg-raised p-2 text-left text-[11px] font-normal leading-snug text-content-tertiary shadow-lg group-hover:block ${align === 'right' ? 'right-0' : 'left-0'}`}
      >
        {children}
      </span>
    </span>
  );
}
